import React, {useEffect, useState} from "react";
import {AlertCircle, CheckCircle, Loader2} from "lucide-react";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import {useDispatch} from "react-redux";
import {Button} from "../components/nuclear/button";
import {setRedirectionPage} from "../features/states/slices";
import SignInPage from "./authentication/SignInPage";
import SignUpPage from "./authentication/SignUpPage";
import ConfirmResetPasswordPage from "./authentication/ConfirmResetPasswordPage";
import {
    confirmUserSignUp,
    fetchCurrentUser,
} from "./authentication/authServices";
import {isUnauthenticatedUserError} from "./authentication/authPageHelpers";
import "./authentication/ConfirmationScreen.module.css";

function getErrorMessage(error) {
    if (typeof error === "string") {
        return error;
    }

    return error?.message ?? "Unable to confirm your account. Please try again.";
}

export function ConfirmationScreen() {
    const [params] = useSearchParams();
    const [confirmation, setConfirmation] = useState();
    const [errorMessage, setErrorMessage] = useState();

    const confirmUser = React.useCallback(async () => {
        try {
            const username = params.get("username");
            const code = params.get("code");

            const response = await confirmUserSignUp({username, code});
            if (response) {
                setConfirmation(true);
            }
        } catch (error) {
            setErrorMessage(error);
        }
    }, [params]);

    useEffect(() => {
        const checkIfLoggedIn = async () => {
            try {
                const response = await fetchCurrentUser();
                if (response) {
                    setConfirmation(false);
                    setErrorMessage({
                        message: "You are already logged in, please sign out to confirm another account",
                    });
                }
            } catch (error) {
                if (isUnauthenticatedUserError(error)) {
                    await confirmUser();
                    return;
                }
                setErrorMessage(error);
            }
        };
        checkIfLoggedIn();
    }, [confirmUser]);

    function ConfirmedAccount() {
        const dispatch = useDispatch();

        useEffect(() => {
            dispatch(setRedirectionPage("survey"));
        }, [dispatch]);

        if (confirmation) {
            return (
                <div className={"auth-confirm-result"}>
                    <div
                        role={"status"}
                        aria-live={"polite"}
                        className={"auth-confirm-message auth-confirm-message--success"}
                    >
                        <CheckCircle size={22} className={"auth-confirm-icon"} aria-hidden={"true"}/>
                        <div>
                            <h1 className={"auth-confirm-heading"}>Account confirmed</h1>
                            <p className={"auth-confirm-copy"}>
                                Your Animotive account is ready. Log in to continue.
                            </p>
                        </div>
                    </div>
                    <Button asChild={true} fullWidth={true}>
                        <RouterLink to={"/login"}>Log In</RouterLink>
                    </Button>
                </div>
            );
        }

        if (!confirmation && params.get("username") !== null && errorMessage === undefined) {
            return (
                <div role={"status"} aria-live={"polite"} className={"auth-confirm-status"}>
                    <Loader2 size={28} className={"auth-confirm-spinner"} aria-hidden={"true"}/>
                    <div>
                        <h1 className={"auth-confirm-heading"}>Confirming your account</h1>
                        <p className={"auth-confirm-copy"}>This should only take a moment.</p>
                    </div>
                </div>
            );
        }

        if (!confirmation && errorMessage) {
            return (
                <div className={"auth-confirm-result"}>
                    <div role={"alert"} className={"auth-confirm-message auth-confirm-message--error"}>
                        <AlertCircle size={22} className={"auth-confirm-icon"} aria-hidden={"true"}/>
                        <div>
                            <h1 className={"auth-confirm-heading"}>We couldn't confirm your account</h1>
                            <p className={"auth-confirm-copy"}>{getErrorMessage(errorMessage)}</p>
                        </div>
                    </div>
                    <Button asChild={true} variant={"outline"} fullWidth={true}>
                        <RouterLink to={"/login"}>Back to Log In</RouterLink>
                    </Button>
                </div>
            );
        }

        if (!confirmation && params.get("username") === null) {
            return (
                <div className={"auth-confirm-result"}>
                    <div role={"alert"} className={"auth-confirm-message auth-confirm-message--error"}>
                        <AlertCircle size={22} className={"auth-confirm-icon"} aria-hidden={"true"}/>
                        <div>
                            <h1 className={"auth-confirm-heading"}>Confirmation link incomplete</h1>
                            <p className={"auth-confirm-copy"}>
                                This link does not include a confirmation code. Please use the complete link from your email.
                            </p>
                        </div>
                    </div>
                    <Button asChild={true} variant={"outline"} fullWidth={true}>
                        <RouterLink to={"/login"}>Back to Log In</RouterLink>
                    </Button>
                </div>
            );
        }

        return null;
    }

    return (
        <section className={"auth-page-shell"}>
            <div className={"auth-page-container"}>
                <img
                    src={"/img/Branding/AnimotiveLogoHeader.png"}
                    alt={"Animotive"}
                    className={"auth-logo"}
                />
                <ConfirmedAccount/>
            </div>
        </section>
    );
}

function getScreenComponent(Screen) {
    if (Screen === "signUp") {
        return SignUpPage;
    }

    if (Screen === "forgotPassword" || Screen === "confirmResetPassword") {
        return ConfirmResetPasswordPage;
    }

    return SignInPage;
}

function Authentication({Screen = "signIn", redirection}) {
    const ScreenComponent = getScreenComponent(Screen);
    return <ScreenComponent redirection={redirection}/>;
}

export default Authentication;
